import { validationErrorMessages } from 'src/app/shared/utility/constants';
import {
  HAWB_ADVANCE_SEARCH_CBP_HELD_PAYLOAD_KEY,
  HAWB_ADVANCE_SEARCH_DELIVERED_CHECKBOX_ID,
  HAWB_ADVANCE_SEARCH_DELIVERED_LABEL,
  HAWB_ADVANCE_SEARCH_HELD_CHECKBOX_ID,
  HAWB_ADVANCE_SEARCH_HELD_LABEL,
  HAWB_ADVANCE_SEARCH_IS_SHIPMENT_ROUTED_LABEL,
  HAWB_ADVANCE_SEARCH_IS_SHIPMENT_ROUTED_PAYLOAD_KEY,
} from './advanced-search-specialsearches/advanced-search-specialsearches.constants';

const SPECIAL_SEARCH_LABELS: Record<string, string> = {
  hawbClearedCustoms: 'Cleared Customs',
  hawbCustomMessagesCheck: 'Does not have custom message',
  hawbPOD: 'POD',
  hawbDestroyed: 'Destroyed',
  [HAWB_ADVANCE_SEARCH_HELD_CHECKBOX_ID]: HAWB_ADVANCE_SEARCH_HELD_LABEL,
  [HAWB_ADVANCE_SEARCH_DELIVERED_CHECKBOX_ID]: HAWB_ADVANCE_SEARCH_DELIVERED_LABEL,
  bagClearedCustoms: 'Cleared Customs',
  bagCustomMessagesCheck: 'Does not have custom message',
  bagRated: 'Rated',
  bagDestroyed: 'Destroyed',
  manifestClearedCustoms: 'Cleared Customs',
  manifestCustomMessagesCheck: 'Does not have custom message',
  manifestPublish: 'Publish',
  manifestRated: 'Rated',
  manifestDestroyed: 'Destroyed',
  mawbClearedCustoms: 'Cleared Customs',
  mawbCustomMessagesCheck: 'Does not have custom message',
  mawbPublish: 'Publish',
  mawbRated: 'Rated',
  mawbDestroyed: 'Destroyed',
  clearedCustoms: 'Cleared Customs',
  noCustomMessages: 'Does not have custom message',
  pod: 'POD',
  destroyed: 'Destroyed',
  [HAWB_ADVANCE_SEARCH_CBP_HELD_PAYLOAD_KEY]: HAWB_ADVANCE_SEARCH_HELD_LABEL,
  isRated: 'Rated',
  isOverageShipment: 'Overage Shipment',
  shipmentOriginCountry: 'Shipment Origin Country',
  carrierType: 'Carrier Type',
  shipmentSelectionPreference: 'Shipment selection preference',
  published: 'Publish',
  [HAWB_ADVANCE_SEARCH_IS_SHIPMENT_ROUTED_PAYLOAD_KEY]:
    HAWB_ADVANCE_SEARCH_IS_SHIPMENT_ROUTED_LABEL,
};

const NEGATED_SPECIAL_SEARCH_LABELS: Record<string, string> = {
  isOverageShipment: 'Not Overage Shipment',
  hawbCustomMessagesCheck: 'Has custom message',
  bagCustomMessagesCheck: 'Has custom message',
  manifestCustomMessagesCheck: 'Has custom message',
  mawbCustomMessagesCheck: 'Has custom message',
  noCustomMessages: 'Has custom message',
};

function isTriStateValue(value: unknown): boolean {
  if (value === true || value === false) {
    return true;
  }
  const normalized = String(value).toLowerCase();
  return normalized === 'true' || normalized === 'false';
}

function formatTriStateLabel(key: string, value: unknown): string | null {
  const label = SPECIAL_SEARCH_LABELS[key];
  if (!label || !isTriStateValue(value)) {
    return null;
  }

  const normalized = String(value).toLowerCase();
  if (normalized === 'true') {
    return label;
  }

  return NEGATED_SPECIAL_SEARCH_LABELS[key] ?? `Not ${label}`;
}

function formatSpecialSearchValue(key: string, value: unknown): string | null {
  const triStateLabel = formatTriStateLabel(key, value);
  if (triStateLabel) {
    return triStateLabel;
  }

  if (key === 'gatewayed') {
    if (value === true || value === 'true') {
      return 'Gatewayed';
    }
    if (value === false || value === 'false') {
      return 'Not gatewayed';
    }
  }

  if (key === 'baggingStatus' && value) {
    const baggingStatus = String(value);
    return baggingStatus === 'NonBagged' ? 'Unbagged' : baggingStatus;
  }

  if (
    (key === 'facility' || key === 'gatewayedfacility') &&
    Array.isArray(value)
  ) {
    const names = value
      .map((item) => {
        if (typeof item === 'object' && item !== null && 'name' in item) {
          return String((item as { name: string }).name);
        }
        return String(item);
      })
      .filter(Boolean);

    if (names.length > 0) {
      return names.join(', ');
    }
  }

  return null;
}

function shouldSkipGenericLeafValue(value: unknown): boolean {
  if (typeof value === 'boolean') {
    return true;
  }

  const normalized = String(value).toLowerCase();
  return normalized === 'true' || normalized === 'false';
}

function collectAdvanceSearchDisplayFields(
  obj: unknown,
  fields: string[]
): void {
  if (!obj || typeof obj !== 'object') {
    return;
  }

  const record = obj as Record<string, unknown>;

  for (const key of Object.keys(record)) {
    const value = record[key];
    if (value === null || value === undefined || value === '') {
      continue;
    }

    if (key === 'userId' || key === 'paging') {
      continue;
    }

    if (
      key === 'specialSearch' &&
      typeof value === 'object' &&
      !Array.isArray(value)
    ) {
      for (const [specialSearchKey, specialSearchValue] of Object.entries(
        value as Record<string, unknown>
      )) {
        const display = formatSpecialSearchValue(
          specialSearchKey,
          specialSearchValue
        );
        if (display) {
          fields.push(display);
        }
      }
      continue;
    }

    if (typeof value === 'object' && !Array.isArray(value)) {
      collectAdvanceSearchDisplayFields(value, fields);
      continue;
    }

    const knownDisplay = formatSpecialSearchValue(key, value);
    if (knownDisplay) {
      fields.push(knownDisplay);
      continue;
    }

    if (!shouldSkipGenericLeafValue(value)) {
      fields.push(String(value));
    }
  }
}

export function buildAdvanceSearchDisplayName(criteria: unknown): string {
  const fields: string[] = [];
  collectAdvanceSearchDisplayFields(criteria, fields);

  if (fields.length === 0) {
    return '';
  }

  if (fields.length === 1) {
    return fields[0];
  }

  return `${fields[0]}${validationErrorMessages.otherCriteria}`;
}
